import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../../context/LocaleContext';
import { searchDirectory } from '../../services/dataStore';
import { DirectorySearchHit } from '../../types';
import OfficialBadge from './OfficialBadge';

interface GlobalSearchProps {
  autoFocus?: boolean;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ autoFocus }) => {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const isEn = locale === 'en';
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    if (query.trim().length < 2) return [];
    return searchDirectory(query, locale, 10);
  }, [query, locale]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const goToHit = (hit: DirectorySearchHit) => {
    setOpen(false);
    setQuery('');
    if (hit.kind === 'country') {
      navigate(`/${locale}/country/${hit.countryId}`);
      return;
    }
    if (hit.citySlug) {
      navigate(`/${locale}/country/${hit.countryId}#city-${hit.citySlug}`);
    }
  };

  const placeholder = isEn
    ? 'Search attractions, cities or countries…'
    : '搜索景点、城市或国家…';

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="flex items-center gap-3 min-h-[4.25rem] px-4 rounded-lg border-2 border-[#d4d2cc] bg-white shadow-sm transition-[border-color,box-shadow] focus-within:border-[var(--dir-coral)] focus-within:ring-2 focus-within:ring-[var(--dir-coral)]/25">
        <span className="text-xl text-[#5c5c5c] shrink-0" aria-hidden>
          ⌕
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className="flex-1 min-w-0 border-0 bg-transparent text-base text-[var(--dir-text)] placeholder:text-[#5c5c5c] focus:outline-none focus:ring-0 py-3"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-[var(--dir-border)] bg-white shadow-md overflow-hidden">
          {results.map((hit, i) => (
            <button
              key={`${hit.kind}-${hit.countryId}-${hit.citySlug}-${hit.attractionLabel}-${i}`}
              type="button"
              onClick={() => goToHit(hit)}
              className="dir-row dir-row-hover w-full text-left px-4 py-3 last:border-0"
            >
              {hit.kind === 'attraction' && hit.attractionLabel ? (
                <div>
                  <div className="font-medium text-[var(--dir-text)]">{hit.attractionLabel}</div>
                  <div className="text-sm text-[var(--dir-muted)] mt-0.5">
                    {hit.cityLabel} · {hit.countryLabel}
                  </div>
                  <div className="mt-2">
                    <OfficialBadge />
                  </div>
                </div>
              ) : hit.kind === 'city' ? (
                <div>
                  <div className="font-medium">{hit.cityLabel}</div>
                  <div className="text-sm text-[var(--dir-muted)]">{hit.countryLabel}</div>
                </div>
              ) : (
                <div className="font-medium">{hit.countryLabel}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
